/**
 * CloudWatch Metrics Dashboard - Superadmin observability page
 * @docs-internal Superadmin access required
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flex, Box, Heading, Text, Card, CardHeader, CardContent, Button, KPICard, LineChart, AreaChart, ChartEmptyState, Select, Skeleton, SkeletonChart, SkeletonKPIRow, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import {
  ArrowSquareOut,
  ArrowClockwise,
  ChartLine,
  Warning,
  CheckCircle,
  Question,
  Bell,
  Lightning,
  Queue,
  Heartbeat,
  Clock,
  Robot,
  Briefcase,
  FlowArrow,
} from '@phosphor-icons/react';
import {
  useMetricsQuery,
  useAlarmsQuery,
  useTimeRangeState,
  useMetricDefinitionsQuery,
  useDimensionsQuery,
  TIME_RANGE_PRESETS,
  alarmStateVariants,
  alarmStateLabels,
  formatRelative,
  type AlarmInfo,
  type MetricSeries,
  type MetricQuery,
  type TimeRangePreset,
} from './useSuperadminData';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

function AlarmStateIcon({ state }: { state: AlarmInfo['state'] }) {
  switch (state) {
    case 'OK':
      return <CheckCircle size={20} className="text-success" weight="fill" />;
    case 'ALARM':
      return <Warning size={20} className="text-destructive" weight="fill" />;
    case 'INSUFFICIENT_DATA':
      return <Question size={20} className="text-muted-foreground" weight="fill" />;
    default:
      return null;
  }
}

function AlarmCard({ alarm, index }: { alarm: AlarmInfo; index: number }) {
  const displayName = alarm.alarmName.replace(/^[^-]+-/, '').replace(/-/g, ' ');
  const isActive = alarm.state === 'ALARM';

  return (
    <motion.div
      variants={fadeInUp}
      initial="initial"
      animate="animate"
      transition={{ delay: index * 0.05 }}
    >
      <Card
        className={`min-w-[220px] transition-all ${
          isActive
            ? 'border-destructive/50 bg-destructive/5 shadow-sm shadow-destructive/10'
            : alarm.state === 'OK'
              ? 'border-success/30'
              : ''
        }`}
      >
        <CardContent className="pt-4 pb-3">
          <Flex align="center" justify="between" className="mb-2">
            <Flex align="center" gap="sm">
              <AlarmStateIcon state={alarm.state} />
              <TagBadge name={alarmStateLabels[alarm.state]} color={badgeVariantToTagColor(alarmStateVariants[alarm.state])} hideDot className={isActive ? 'animate-pulse' : ''} />
            </Flex>
          </Flex>
          <Heading level={4} className="capitalize mb-1 text-sm font-semibold">
            {displayName}
          </Heading>
          {alarm.alarmDescription && (
            <Text size="xs" className="text-muted-foreground mb-2 line-clamp-2">
              {alarm.alarmDescription}
            </Text>
          )}
          {alarm.stateUpdatedTimestamp && (
            <Flex align="center" gap="xs" className="text-muted-foreground">
              <Clock size={12} />
              <Text size="xs">{formatRelative(alarm.stateUpdatedTimestamp)}</Text>
            </Flex>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function MetricChart({
  title,
  series,
  color,
  type = 'line',
  isLoading,
}: {
  title: string;
  series: MetricSeries | null | undefined;
  color?: string;
  type?: 'line' | 'area';
  isLoading?: boolean;
}) {
  const chartData = React.useMemo(() => {
    if (!series?.dataPoints.length) return [];
    return series.dataPoints.map((dp) => ({
      time: new Date(dp.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      value: dp.value,
    }));
  }, [series]);

  const isEmpty = !chartData.length;

  if (isLoading) {
    return <SkeletonChart height={200} showTitle />;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <Heading level={4} className="text-sm font-medium">
          {title}
        </Heading>
      </CardHeader>
      <CardContent className="pt-0">
        {isEmpty ? (
          <ChartEmptyState
            message="No data"
            description="Metrics will appear when data is collected"
            icon={type === 'area' ? 'bar' : 'line'}
            height={200}
          />
        ) : type === 'area' ? (
          <AreaChart
            data={chartData}
            xDataKey="time"
            areas={[
              {
                dataKey: 'value',
                name: series?.label ?? title,
                color: color ?? undefined,
              },
            ]}
            height={200}
          />
        ) : (
          <LineChart
            data={chartData}
            xDataKey="time"
            lines={[
              {
                dataKey: 'value',
                name: series?.label ?? title,
                color: color ?? undefined,
              },
            ]}
            height={200}
            showLegend={false}
          />
        )}
      </CardContent>
    </Card>
  );
}

function MultiMetricChart({
  title,
  series,
  colors,
  isLoading,
}: {
  title: string;
  series: { data: MetricSeries | null | undefined; name: string }[];
  colors?: string[];
  isLoading?: boolean;
}) {
  const chartData = React.useMemo(() => {
    const timestamps = new Set<string>();
    series.forEach((s) => {
      s.data?.dataPoints.forEach((dp) => timestamps.add(dp.timestamp));
    });

    const sortedTimestamps = Array.from(timestamps).sort();

    return sortedTimestamps.map((ts) => {
      const point: Record<string, unknown> = {
        time: new Date(ts).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      };
      series.forEach((s) => {
        const dp = s.data?.dataPoints.find((d) => d.timestamp === ts);
        point[s.name] = dp?.value ?? null;
      });
      return point;
    });
  }, [series]);

  const isEmpty = !chartData.length;

  if (isLoading) {
    return <SkeletonChart height={200} showTitle />;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <Heading level={4} className="text-sm font-medium">
          {title}
        </Heading>
      </CardHeader>
      <CardContent className="pt-0">
        {isEmpty ? (
          <ChartEmptyState
            message="No data"
            description="Metrics will appear when data is collected"
            icon="line"
            height={200}
          />
        ) : (
          <LineChart
            data={chartData}
            xDataKey="time"
            lines={series.map((s, i) => ({
              dataKey: s.name,
              name: s.name,
              color: colors?.[i] ?? undefined,
            }))}
            height={200}
          />
        )}
      </CardContent>
    </Card>
  );
}

function KPISummaryRow({
  metrics,
  alarmCounts,
  isLoading,
}: {
  metrics: MetricSeries[];
  alarmCounts: { OK: number; ALARM: number; INSUFFICIENT_DATA: number };
  isLoading: boolean;
}) {
  if (isLoading) {
    return <SkeletonKPIRow count={4} />;
  }

  // Find metrics by ID
  const queueDepth = metrics.find((m) => m.id === 'queue_depth');
  const runningJobs = metrics.find((m) => m.id === 'running_jobs');

  const avgQueueDepth = React.useMemo(() => {
    const dataPoints = queueDepth?.dataPoints ?? [];
    if (dataPoints.length === 0) return 0;
    return Math.round(dataPoints.reduce((sum, dp) => sum + dp.value, 0) / dataPoints.length);
  }, [queueDepth]);

  const avgRunningJobs = React.useMemo(() => {
    const dataPoints = runningJobs?.dataPoints ?? [];
    if (dataPoints.length === 0) return 0;
    return (
      Math.round((dataPoints.reduce((sum, dp) => sum + dp.value, 0) / dataPoints.length) * 10) / 10
    );
  }, [runningJobs]);

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="grid grid-cols-2 md:grid-cols-4 gap-4"
    >
      <motion.div variants={fadeInUp}>
        <KPICard
          value={avgQueueDepth}
          title="Avg Queue Depth"
          subtitle="Jobs waiting"
          icon={<Queue size={16} weight="duotone" />}
          variant={avgQueueDepth > 10 ? 'warning' : 'default'}
          size="sm"
        />
      </motion.div>
      <motion.div variants={fadeInUp}>
        <KPICard
          value={avgRunningJobs}
          title="Avg Running Jobs"
          subtitle="Concurrent jobs"
          icon={<Lightning size={16} weight="duotone" />}
          variant="default"
          size="sm"
        />
      </motion.div>
      <motion.div variants={fadeInUp}>
        <KPICard
          value={metrics.length}
          title="Metrics Loaded"
          subtitle="Data series"
          icon={<ChartLine size={16} weight="duotone" />}
          variant="default"
          size="sm"
        />
      </motion.div>
      <motion.div variants={fadeInUp}>
        <KPICard
          value={alarmCounts.ALARM}
          title="Active Alarms"
          subtitle={`${alarmCounts.OK} healthy`}
          icon={<Bell size={16} weight="duotone" />}
          variant={alarmCounts.ALARM > 0 ? 'destructive' : 'success'}
          size="sm"
        />
      </motion.div>
    </motion.div>
  );
}

function AlarmStatusSection({
  alarms,
  alarmCounts,
  isLoading,
}: {
  alarms: AlarmInfo[];
  alarmCounts: { OK: number; ALARM: number; INSUFFICIENT_DATA: number };
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Box>
        <Flex align="center" gap="sm" className="mb-4">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={120} height={24} />
        </Flex>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} />
          ))}
        </div>
      </Box>
    );
  }

  // Sort alarms: ALARM first, then OK, then INSUFFICIENT_DATA
  const sortedAlarms = React.useMemo(() => {
    const order = { ALARM: 0, OK: 1, INSUFFICIENT_DATA: 2 };
    return [...alarms].sort((a, b) => order[a.state] - order[b.state]);
  }, [alarms]);

  return (
    <Box>
      <Flex align="center" gap="sm" className="mb-4">
        <Bell size={20} className="text-muted-foreground" weight="duotone" />
        <Heading level={3} className="text-base font-semibold">
          Alarm Status
        </Heading>
        {alarmCounts.ALARM > 0 && (
          <TagBadge name={<>{alarmCounts.ALARM} Active</>} color="red" hideDot className="animate-pulse" />
        )}
        {alarmCounts.INSUFFICIENT_DATA > 0 && (
          <TagBadge name={<>{alarmCounts.INSUFFICIENT_DATA} No Data</>} color="gray" hideDot />
        )}
      </Flex>

      {alarms.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8">
            <Flex justify="center" align="center" direction="column" gap="sm">
              <Bell size={32} weight="duotone" className="text-muted-foreground/50" />
              <Text className="text-muted-foreground">No alarms configured</Text>
              <Text size="sm" className="text-muted-foreground/70">
                CloudWatch alarms will appear here when configured
              </Text>
            </Flex>
          </CardContent>
        </Card>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {sortedAlarms.map((alarm, index) => (
            <AlarmCard key={alarm.alarmName} alarm={alarm} index={index} />
          ))}
        </motion.div>
      )}
    </Box>
  );
}

function ChartSection({
  title,
  icon,
  children,
  headerRight,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  return (
    <Box>
      <Flex align="center" justify="between" className="mb-4">
        <Flex align="center" gap="sm">
          {icon}
          <Heading level={3} className="text-base font-semibold">
            {title}
          </Heading>
        </Flex>
        {headerRight}
      </Flex>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
    </Box>
  );
}

/**
 * Dimension selector component for filtering metrics
 */
function DimensionSelector({
  namespace,
  selectedDimensions,
  onDimensionChange,
}: {
  namespace: string;
  selectedDimensions: Record<string, string>;
  onDimensionChange: (dims: Record<string, string>) => void;
}) {
  const { data: dimensionsData, isLoading } = useDimensionsQuery(namespace);
  const dimensions = dimensionsData?.dimensions ?? [];

  if (isLoading) {
    return (
      <Flex gap="sm">
        <Skeleton variant="rounded" width={150} height={32} />
        <Skeleton variant="rounded" width={150} height={32} />
      </Flex>
    );
  }

  if (dimensions.length === 0) {
    return null;
  }

  return (
    <Flex gap="sm" wrap="wrap">
      {dimensions.map((dim) => (
        <Select
          key={dim.name}
          label=""
          aria-label={dim.name}
          placeholder={`Select ${dim.name}`}
          items={[
            { id: '', label: `All ${dim.name}s` },
            ...dim.values.map((v) => ({ id: v, label: v })),
          ]}
          selectedKey={selectedDimensions[dim.name] ?? ''}
          onSelectionChange={(key) => {
            const newDims = { ...selectedDimensions };
            if (key === '') {
              delete newDims[dim.name];
            } else {
              newDims[dim.name] = String(key);
            }
            onDimensionChange(newDims);
          }}
          className="min-w-[150px]"
        />
      ))}
    </Flex>
  );
}

/**
 * Agent metrics section with dimension selectors
 */
function AgentMetricsSection({
  timeRange,
  period,
}: {
  timeRange: { start: Date; end: Date };
  period: number;
}) {
  const [dimensions, setDimensions] = React.useState<Record<string, string>>({});
  const definitionsQuery = useMetricDefinitionsQuery();
  const definitions = definitionsQuery.data?.definitions ?? [];

  // Build queries for agent metrics with selected dimensions
  const agentQueries = React.useMemo<MetricQuery[]>(() => {
    // Only query if we have all required dimensions selected
    const agentDefs = definitions.filter((d) => d.namespace === 'Agents');
    if (agentDefs.length === 0) return [];

    const requiredDims = agentDefs[0]?.dimensionNames ?? [];
    const hasAllDims = requiredDims.every((dim) => dimensions[dim]);

    if (!hasAllDims) return [];

    return agentDefs.map((def) => ({
      id: def.id,
      namespace: def.namespace,
      metricName: def.metricName,
      stat: def.stat,
      label: def.label,
      dimensions,
    }));
  }, [definitions, dimensions]);

  const metricsQuery = useMetricsQuery(timeRange, period, agentQueries, agentQueries.length > 0);
  const metrics = metricsQuery.data?.metrics ?? [];

  const invocations = metrics.find((m) => m.id === 'agent_invocations');
  const successes = metrics.find((m) => m.id === 'agent_successes');
  const duration = metrics.find((m) => m.id === 'agent_duration');
  const tokensTotal = metrics.find((m) => m.id === 'tokens_total');

  const noDimensionsSelected = agentQueries.length === 0;

  return (
    <ChartSection
      title="Agent Metrics"
      icon={<Robot size={20} className="text-muted-foreground" weight="duotone" />}
      headerRight={
        <DimensionSelector
          namespace="Agents"
          selectedDimensions={dimensions}
          onDimensionChange={setDimensions}
        />
      }
    >
      {noDimensionsSelected ? (
        <Card className="col-span-2 border-dashed">
          <CardContent className="py-8">
            <Flex justify="center" align="center" direction="column" gap="sm">
              <Robot size={32} weight="duotone" className="text-muted-foreground/50" />
              <Text className="text-muted-foreground">Select dimensions to view agent metrics</Text>
              <Text size="sm" className="text-muted-foreground/70">
                Choose a Label and ModelId from the dropdowns above
              </Text>
            </Flex>
          </CardContent>
        </Card>
      ) : (
        <>
          <MultiMetricChart
            title="Agent Activity"
            series={[
              { data: invocations, name: 'Invocations' },
              { data: successes, name: 'Successes' },
            ]}
            colors={['#3b82f6', '#22c55e']}
            isLoading={metricsQuery.isLoading}
          />
          <MetricChart
            title="Average Duration (ms)"
            series={duration}
            color="#8b5cf6"
            isLoading={metricsQuery.isLoading}
          />
          <MetricChart
            title="Total Tokens"
            series={tokensTotal}
            color="#f59e0b"
            isLoading={metricsQuery.isLoading}
          />
        </>
      )}
    </ChartSection>
  );
}

/**
 * Job metrics section with dimension selectors
 */
function JobMetricsSection({
  timeRange,
  period,
}: {
  timeRange: { start: Date; end: Date };
  period: number;
}) {
  const [dimensions, setDimensions] = React.useState<Record<string, string>>({});
  const definitionsQuery = useMetricDefinitionsQuery();
  const definitions = definitionsQuery.data?.definitions ?? [];

  // Build queries for job metrics with selected dimensions
  const jobQueries = React.useMemo<MetricQuery[]>(() => {
    const jobDefs = definitions.filter(
      (d) => d.namespace === 'Jobs' && d.dimensionNames.includes('JobType')
    );
    if (jobDefs.length === 0) return [];

    const hasJobType = Boolean(dimensions['JobType']);
    if (!hasJobType) return [];

    return jobDefs.map((def) => ({
      id: def.id,
      namespace: def.namespace,
      metricName: def.metricName,
      stat: def.stat,
      label: def.label,
      dimensions,
    }));
  }, [definitions, dimensions]);

  const metricsQuery = useMetricsQuery(timeRange, period, jobQueries, jobQueries.length > 0);
  const metrics = metricsQuery.data?.metrics ?? [];

  const started = metrics.find((m) => m.id === 'jobs_started');
  const completed = metrics.find((m) => m.id === 'jobs_completed');
  const duration = metrics.find((m) => m.id === 'job_duration');
  const queueTime = metrics.find((m) => m.id === 'job_queue_time');

  const noDimensionsSelected = jobQueries.length === 0;

  return (
    <ChartSection
      title="Job Metrics (by Type)"
      icon={<Briefcase size={20} className="text-muted-foreground" weight="duotone" />}
      headerRight={
        <DimensionSelector
          namespace="Jobs"
          selectedDimensions={dimensions}
          onDimensionChange={setDimensions}
        />
      }
    >
      {noDimensionsSelected ? (
        <Card className="col-span-2 border-dashed">
          <CardContent className="py-8">
            <Flex justify="center" align="center" direction="column" gap="sm">
              <Briefcase size={32} weight="duotone" className="text-muted-foreground/50" />
              <Text className="text-muted-foreground">Select a job type to view metrics</Text>
              <Text size="sm" className="text-muted-foreground/70">
                Choose a JobType from the dropdown above
              </Text>
            </Flex>
          </CardContent>
        </Card>
      ) : (
        <>
          <MultiMetricChart
            title="Job Activity"
            series={[
              { data: started, name: 'Started' },
              { data: completed, name: 'Completed' },
            ]}
            colors={['#3b82f6', '#22c55e']}
            isLoading={metricsQuery.isLoading}
          />
          <MetricChart
            title="Average Duration (ms)"
            series={duration}
            color="#8b5cf6"
            isLoading={metricsQuery.isLoading}
          />
          <MetricChart
            title="Average Queue Time (ms)"
            series={queueTime}
            color="#f59e0b"
            isLoading={metricsQuery.isLoading}
          />
        </>
      )}
    </ChartSection>
  );
}

export function SuperadminMetrics() {
  const timeRangeState = useTimeRangeState('24h');
  const { preset, setPreset, timeRange, period } = timeRangeState;

  // System metrics (no dimensions required)
  const systemMetricsQuery = useMetricsQuery(timeRange, period);
  const alarmsQuery = useAlarmsQuery();

  const handleRefresh = () => {
    systemMetricsQuery.refetch();
    alarmsQuery.refetch();
  };

  const handlePresetChange = (value: string) => {
    setPreset(value as TimeRangePreset);
  };

  const isLoading = systemMetricsQuery.isLoading || alarmsQuery.isLoading;
  const metrics = systemMetricsQuery.data?.metrics ?? [];
  const alarms = alarmsQuery.data?.alarms ?? [];
  const cloudWatchUrl = alarmsQuery.data?.cloudWatchConsoleUrl;

  const alarmCounts = React.useMemo(() => {
    const counts = { OK: 0, ALARM: 0, INSUFFICIENT_DATA: 0 };
    alarms.forEach((a) => {
      counts[a.state]++;
    });
    return counts;
  }, [alarms]);

  // Find system metrics by ID
  const queueDepth = metrics.find((m) => m.id === 'queue_depth');
  const runningJobs = metrics.find((m) => m.id === 'running_jobs');
  const transientErrors = metrics.find((m) => m.id === 'transient_network_errors');

  // Workflow cache metrics
  const cacheActionHits = metrics.find((m) => m.id === 'cache_action_hits');
  const cacheActionMisses = metrics.find((m) => m.id === 'cache_action_misses');

  return (
    <Box className="space-y-6">
      {/* Header with prominent time controls */}
      <Card className="bg-gradient-to-r from-card to-muted/30 border-muted">
        <CardContent className="py-4">
          <Flex justify="between" align="center" wrap="wrap" gap="md">
            <Flex align="center" gap="sm">
              <div className="p-2 rounded-lg bg-primary/10">
                <ChartLine size={24} className="text-primary" weight="duotone" />
              </div>
              <div>
                <Heading level={2} className="text-lg">
                  CloudWatch Metrics
                </Heading>
                <Text size="sm" className="text-muted-foreground">
                  Real-time system observability
                </Text>
              </div>
            </Flex>

            {/* Control bar - grouped buttons */}
            <div className="flex items-center rounded-lg border border-border bg-background shadow-sm overflow-hidden">
              <Select
                label=""
                aria-label="Time range"
                placeholder="Select range"
                items={TIME_RANGE_PRESETS.filter((p) => p.value !== 'custom').map((p) => ({
                  id: p.value,
                  label: p.label,
                }))}
                selectedKey={preset}
                onSelectionChange={(key) => handlePresetChange(String(key))}
                className="min-w-[140px] border-0 rounded-none bg-transparent h-8 text-sm"
              />
              <div className="w-px h-5 bg-border" />
              <Button
                variant="icon"
                size="sm"
                iconOnly
                onPress={handleRefresh}
                isDisabled={isLoading}
                tooltip="Refresh"
              >
                <ArrowClockwise size={16} className={isLoading ? 'animate-spin' : ''} />
              </Button>
              {cloudWatchUrl && (
                <>
                  <div className="w-px h-5 bg-border" />
                  <Button
                    variant="icon"
                    size="sm"
                    iconOnly
                    onPress={() => window.open(cloudWatchUrl, '_blank')}
                    tooltip="Open CloudWatch"
                  >
                    <ArrowSquareOut size={16} />
                  </Button>
                </>
              )}
            </div>
          </Flex>
        </CardContent>
      </Card>

      {/* KPI Summary Cards */}
      <KPISummaryRow metrics={metrics} alarmCounts={alarmCounts} isLoading={isLoading} />

      {/* Alarm Status */}
      <AlarmStatusSection alarms={alarms} alarmCounts={alarmCounts} isLoading={isLoading} />

      {/* System Metrics (no dimensions) */}
      <ChartSection
        title="System Metrics"
        icon={<Heartbeat size={20} className="text-muted-foreground" weight="duotone" />}
      >
        <MetricChart
          title="Queue Depth"
          series={queueDepth}
          type="area"
          color="#8b5cf6"
          isLoading={isLoading}
        />
        <MetricChart
          title="Running Jobs"
          series={runningJobs}
          type="area"
          color="#06b6d4"
          isLoading={isLoading}
        />
        <MetricChart
          title="Transient Network Errors"
          series={transientErrors}
          type="area"
          color="#ef4444"
          isLoading={isLoading}
        />
      </ChartSection>

      {/* Workflow Cache Metrics (no dimensions) */}
      <ChartSection
        title="Workflow Cache"
        icon={<FlowArrow size={20} className="text-muted-foreground" weight="duotone" />}
      >
        <MultiMetricChart
          title="Cache Action Hits vs Misses"
          series={[
            { data: cacheActionHits, name: 'Hits' },
            { data: cacheActionMisses, name: 'Misses' },
          ]}
          colors={['#4DA6FF', '#FFBD0F']}
          isLoading={isLoading}
        />
      </ChartSection>

      {/* Agent Metrics (with dimension selectors) */}
      <AgentMetricsSection timeRange={timeRange} period={period} />

      {/* Job Metrics (with dimension selectors) */}
      <JobMetricsSection timeRange={timeRange} period={period} />

      {/* Footer Metadata */}
      <AnimatePresence>
        {systemMetricsQuery.data && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="bg-muted/30 border-muted">
              <CardContent className="py-3">
                <Flex gap="md" wrap="wrap" align="center" className="text-sm text-muted-foreground">
                  <Flex align="center" gap="xs">
                    <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                    <Text size="sm">Connected</Text>
                  </Flex>
                  <Text size="sm">
                    Namespace:{' '}
                    <span className="font-mono text-foreground">
                      {systemMetricsQuery.data.namespacePrefix}
                    </span>
                  </Text>
                  <Text size="sm">Period: {systemMetricsQuery.data.period}s</Text>
                  <Text size="sm">Updated: {new Date().toLocaleTimeString()}</Text>
                </Flex>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
